export type CapacityFilterType = {
  capacityMax?: number;
  capacityMin?: number;
};

type CapacityFilterTypeWithNulls = {
  [K in keyof CapacityFilterType]?: CapacityFilterType[K] | null;
};

/**
 * Class size keys for iteration
 */
export const ClassSizes = ["oneOnOne", "smallGroup"] as const;
export type ClassSize = (typeof ClassSizes)[number];

/**
 * Class size filtering utilities for search functionality.
 *
 * This module provides functions to convert between class size strings
 * and capacity filter objects used in search queries.
 */

/**
 * Converts capacity filter object to a single class size string.
 *
 * @param capacityFilters - The capacity filter object with min/max values
 * @returns A class size string: "oneOnOne", "smallGroup", or "any"
 */
export function getClassSizeFromCapacityFilter(
  capacityFilters?: CapacityFilterTypeWithNulls,
): ClassSize | "any" {
  if (capacityFilters?.capacityMax === 1) {
    return "oneOnOne";
  }
  if (capacityFilters?.capacityMin === 2) {
    return "smallGroup";
  }
  return "any";
}

/**
 * Converts a single class size string to capacity filter object.
 *
 * @param classSize - The class size string ("oneOnOne", "smallGroup", or "any")
 * @returns A capacity filter object
 */
export function getCapacityFilterFromClassSize(
  classSize: ClassSize | "any",
): CapacityFilterType {
  switch (classSize) {
    case "oneOnOne":
      return { capacityMin: undefined, capacityMax: 1 };
    case "smallGroup":
      return { capacityMin: 2, capacityMax: undefined };
    case "any":
      return { capacityMin: undefined, capacityMax: undefined };
  }
}

/**
 * Converts capacity filter object to an array of class size strings.
 *
 * @param capacityFilters - The capacity filter object with min/max values
 * @returns An array of class size strings
 */
export function getClassSizesFromCapacityFilter(
  capacityFilters?: CapacityFilterTypeWithNulls,
): ClassSize[] {
  const sizes: ClassSize[] = [];
  if (capacityFilters?.capacityMax === 1) {
    sizes.push("oneOnOne");
  }
  // These two shouldn't ever be used together, so else if.
  else if (capacityFilters?.capacityMin === 2) {
    sizes.push("smallGroup");
  }
  return sizes;
}

/**
 * Converts an array of class size strings to capacity filter object.
 *
 * @param classSizes - Array of class size strings
 * @returns A capacity filter object
 */
export function getCapacityFiltersFromClassSize(
  classSizes: ClassSize[],
): CapacityFilterType {
  const hasSmallGroup = classSizes.includes("smallGroup");
  const hasOneOnOne = classSizes.includes("oneOnOne");
  if (hasOneOnOne && !hasSmallGroup) {
    return { capacityMin: undefined, capacityMax: 1 };
  }
  if (hasSmallGroup && !hasOneOnOne) {
    return { capacityMin: 2, capacityMax: undefined };
  }
  return { capacityMin: undefined, capacityMax: undefined };
}
